import React, { useEffect, useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { CheckCircle, XCircle, Send, FileText } from 'lucide-react';
import api from '../utils/api';
import { PageHeader, Card, Badge, LoadingSpinner, Table } from '../components/UI';

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/logs', { params });
      setLogs(res.data);
    } catch(e) {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      header: 'Report', key: 'report_name',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: row.status === 'sent' ? '#dbeafe' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {row.status === 'sent' ? <Send size={14} color="var(--accent)" /> : <XCircle size={14} color="var(--danger)" />}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>{v}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Due: {row.report_due_date ? format(parseISO(row.report_due_date), 'MMM d, yyyy') : '—'}</div>
          </div>
        </div>
      )
    },
    { header: 'Sent At', key: 'sent_at', render: v => v ? <span style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)' }}>{format(parseISO(v), 'MMM d, yyyy h:mm a')}</span> : '—' },
    { header: 'Recipients', key: 'recipients', render: v => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v}</span> },
    { header: 'Status', key: 'status', render: v => <Badge label={v} type={v === 'sent' ? 'success' : 'danger'} /> },
    {
      header: 'Error', key: 'error_message',
      render: v => v ? <span style={{ fontSize: 12, color: 'var(--danger)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{v}</span> : <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>—</span>
    },
  ];

  const sent = logs.filter(l => l.status === 'sent').length;
  const failed = logs.filter(l => l.status === 'failed').length;

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1200, margin: '0 auto' }} className="fade-in">
      <PageHeader title="Email Logs" subtitle="History of all reminder emails sent" />

      {/* Mini stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: logs.length, icon: FileText, color: 'var(--primary)' },
          { label: 'Sent', value: sent, icon: CheckCircle, color: 'var(--success)' },
          { label: 'Failed', value: failed, icon: XCircle, color: 'var(--danger)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{ padding: '14px 20px', background: 'var(--bg-card)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon size={15} color={color} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</div>
            </div>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 'auto', minWidth: 120 }}>
            <option value="">All Status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? <LoadingSpinner /> : (
          <Table columns={columns} data={logs} emptyMessage="No email logs found." />
        )}
      </Card>
    </div>
  );
}
